// Query Storage Utility - stores user queries and dev feedback in localStorage

const QUERY_HISTORY_KEY = 'poolbrain_query_history';

// ============================================================================
// Query History - Logged by chatbot for each user query
// ============================================================================

export const saveQueryToHistory = (queryData) => {
  try {
    const history = getQueryHistory();
    const entry = {
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      originalQuery: queryData.originalQuery || '',
      normalizedQuery: queryData.normalizedQuery || '',
      generatedSQL: queryData.generatedSQL || '',
      queryCategory: queryData.queryCategory || '',
      ragContext: queryData.ragContext || '',
      companyId: queryData.companyId || 'all',
      sessionId: queryData.sessionId || '',
      hasError: queryData.hasError || false,
      errorMessage: queryData.errorMessage || '',
      // Dev feedback fields - to be filled by developer
      devFeedback: null
    };

    history.unshift(entry); // Add to beginning (newest first)

    // Keep only last 500 queries to prevent localStorage overflow
    const trimmedHistory = history.slice(0, 500);

    localStorage.setItem(QUERY_HISTORY_KEY, JSON.stringify(trimmedHistory));
    return entry.id;
  } catch (error) {
    console.error('Failed to save query to history:', error);
    return null;
  }
};

export const getQueryHistory = () => {
  try {
    const stored = localStorage.getItem(QUERY_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to get query history:', error);
    return [];
  }
};

export const clearQueryHistory = () => {
  try {
    localStorage.removeItem(QUERY_HISTORY_KEY);
    return true;
  } catch (error) {
    console.error('Failed to clear query history:', error);
    return false;
  }
};

export const deleteQuery = (queryId) => {
  try {
    const history = getQueryHistory();
    const filteredHistory = history.filter(q => q.id !== queryId);
    localStorage.setItem(QUERY_HISTORY_KEY, JSON.stringify(filteredHistory));
    return true;
  } catch (error) {
    console.error('Failed to delete query:', error);
    return false;
  }
};

// ============================================================================
// Developer Feedback - Added by dev for specific queries
// ============================================================================

export const saveDevFeedback = (queryId, feedback) => {
  try {
    const history = getQueryHistory();
    const queryIndex = history.findIndex(q => q.id === queryId);

    if (queryIndex !== -1) {
      history[queryIndex].devFeedback = {
        isCorrect: feedback.isCorrect,
        errorType: feedback.errorType || '',
        tableIssues: feedback.tableIssues || '',
        joinIssues: feedback.joinIssues || '',
        columnIssues: feedback.columnIssues || '',
        correctSQL: feedback.correctSQL || '',
        notes: feedback.notes || '',
        reviewedAt: new Date().toISOString()
      };

      localStorage.setItem(QUERY_HISTORY_KEY, JSON.stringify(history));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to save dev feedback:', error);
    return false;
  }
};

export const getQueryById = (queryId) => {
  const history = getQueryHistory();
  return history.find(q => q.id === queryId) || null;
};

export const markAddedToSchema = (queryId, businessLogicNumber) => {
  try {
    const history = getQueryHistory();
    const queryIndex = history.findIndex(q => q.id === queryId);

    if (queryIndex !== -1) {
      history[queryIndex].addedToSchema = {
        businessLogicNumber: businessLogicNumber,
        addedAt: new Date().toISOString()
      };

      localStorage.setItem(QUERY_HISTORY_KEY, JSON.stringify(history));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to mark as added to schema:', error);
    return false;
  }
};

// ============================================================================
// Statistics
// ============================================================================

export const getQueryStats = () => {
  const history = getQueryHistory();
  const reviewed = history.filter(q => q.devFeedback !== null);
  const correct = reviewed.filter(q => q.devFeedback?.isCorrect === true);
  const incorrect = reviewed.filter(q => q.devFeedback?.isCorrect === false);

  // Group by category
  const byCategory = {};
  history.forEach(q => {
    const cat = q.queryCategory || 'unknown';
    if (!byCategory[cat]) {
      byCategory[cat] = { total: 0, reviewed: 0, correct: 0 };
    }
    byCategory[cat].total++;
    if (q.devFeedback) {
      byCategory[cat].reviewed++;
      if (q.devFeedback.isCorrect) byCategory[cat].correct++;
    }
  });

  // Group by error type
  const byErrorType = {};
  incorrect.forEach(q => {
    const errType = q.devFeedback?.errorType || 'other';
    byErrorType[errType] = (byErrorType[errType] || 0) + 1;
  });

  return {
    totalQueries: history.length,
    reviewedCount: reviewed.length,
    pendingReview: history.length - reviewed.length,
    correctCount: correct.length,
    incorrectCount: incorrect.length,
    accuracyRate: reviewed.length > 0 ? (correct.length / reviewed.length * 100).toFixed(1) : 0,
    byCategory,
    byErrorType
  };
};

// ============================================================================
// Export for analysis
// ============================================================================

export const exportQueryHistory = () => {
  const history = getQueryHistory();
  const blob = new Blob([JSON.stringify(history, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `query_history_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
